const DEFAULT_SEGMENT_TRANSITION_DURATION_SECONDS = 1.6;

export enum SegmentTransitionType {
  FADE = 'fade'
}

export type SegmentTransitionRenderStrategy = 'non_overlap' | 'overlap';

export type SegmentTransitionPropertiesByType = {
  [SegmentTransitionType.FADE]: {
    audioDip?: boolean;
    easing?: 'linear' | 'easeInOut';
  };
};

type SegmentBoundaryTransitionBase<T extends SegmentTransitionType = SegmentTransitionType> = {
  transitionType: T;
  fromSegmentId?: string;
  toSegmentId?: string;
  durationInFrames: number;
  outDurationInFrames?: number;
  inDurationInFrames?: number;
};

export type SegmentBoundaryTransition = {
  [K in SegmentTransitionType]: SegmentBoundaryTransitionBase<K> & {
    properties?: SegmentTransitionPropertiesByType[K];
  };
}[SegmentTransitionType];

export type SegmentTransitionBoundaryIds =
  | { fromSegmentId: string; toSegmentId: string }
  | { fromSegmentId: string; toSegmentId?: undefined }
  | { fromSegmentId?: undefined; toSegmentId: string };

export type SegmentTransitionPresetMap = Record<
  SegmentTransitionType,
  {
    displayName: string;
    renderStrategy: SegmentTransitionRenderStrategy;
    defaultDurationInFrames: (fps: number) => number;
    defaultOutDurationInFrames?: (fps: number) => number;
    defaultInDurationInFrames?: (fps: number) => number;
    defaultProperties?: SegmentTransitionPropertiesByType[SegmentTransitionType];
  }
>;

export const SEGMENT_TRANSITION_PRESET_MAP: SegmentTransitionPresetMap = {
  [SegmentTransitionType.FADE]: {
    displayName: 'Fade',
    renderStrategy: 'non_overlap',
    defaultDurationInFrames: fps => Math.round(DEFAULT_SEGMENT_TRANSITION_DURATION_SECONDS * fps),
    defaultOutDurationInFrames: fps => Math.round((DEFAULT_SEGMENT_TRANSITION_DURATION_SECONDS / 2) * fps),
    defaultInDurationInFrames: fps => Math.round((DEFAULT_SEGMENT_TRANSITION_DURATION_SECONDS / 2) * fps),
    defaultProperties: {
      audioDip: true,
      easing: 'easeInOut'
    }
  }
};

export const DEFAULT_SEGMENT_TRANSITION_TYPE: SegmentTransitionType = SegmentTransitionType.FADE;

/** `createSegmentTransition` overrides for highlight-reel boundaries: a shorter fade than the
 *  global default and no audio dip, so words at the tail of one segment or the head of the next are
 *  neither blacked out nor silenced. Shared by the transitions auto-seeded for a fresh reel and the
 *  manual per-boundary toggle in content-studio. */
export const REEL_SEGMENT_TRANSITION_OVERRIDES = {
  durationSeconds: 0.6,
  properties: { audioDip: false }
};

export const createSegmentTransition = ({
  transitionType = DEFAULT_SEGMENT_TRANSITION_TYPE,
  fromSegmentId,
  toSegmentId,
  fps,
  durationSeconds,
  properties
}: SegmentTransitionBoundaryIds & {
  transitionType?: SegmentTransitionType;
  fps: number;
  durationSeconds?: number;
  properties?: SegmentTransitionPropertiesByType[SegmentTransitionType];
}): SegmentBoundaryTransition => {
  if (!fromSegmentId && !toSegmentId) {
    throw new Error('Segment transition requires at least one boundary segment id');
  }

  const preset = SEGMENT_TRANSITION_PRESET_MAP[transitionType];
  const halfDurationInFrames = durationSeconds === undefined ? undefined : Math.round((durationSeconds / 2) * fps);

  return {
    transitionType,
    fromSegmentId,
    toSegmentId,
    durationInFrames:
      durationSeconds === undefined ? preset.defaultDurationInFrames(fps) : Math.round(durationSeconds * fps),
    outDurationInFrames: halfDurationInFrames ?? preset.defaultOutDurationInFrames?.(fps),
    inDurationInFrames: halfDurationInFrames ?? preset.defaultInDurationInFrames?.(fps),
    properties: { ...preset.defaultProperties, ...properties }
  };
};
